// src/components/PrivacyPolicy.jsx
import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

export default function PrivacyPolicy() {
  const navigate = useNavigate()

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  return (
    <div style={{
      minHeight: '100vh',
      background: '#04060d',
      color: '#dce8f5',
      fontFamily: "'IBM Plex Sans', sans-serif",
      WebkitFontSmoothing: 'antialiased',
    }}>
      <link href="https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500;600&family=IBM+Plex+Sans:wght@300;400;500;600;700&display=swap" rel="stylesheet" />

      {/* ── NAV ── */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 10,
        height: 52, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 28px',
        background: 'rgba(4,6,13,0.9)', backdropFilter: 'blur(12px)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        </div>
        <button onClick={() => navigate('/')} style={{
          display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px',
          background: 'none', border: 'none',
          color: '#00d4b8', fontSize: 13, cursor: 'pointer',
          fontFamily: "'IBM Plex Mono', monospace", fontWeight: 500,
          transition: 'all .2s',
        }}
          onMouseEnter={e => { 
            e.currentTarget.style.background='rgba(0,212,184,0.1)'; 
            e.currentTarget.style.color='#00d4b8';
          }}
          onMouseLeave={e => { 
            e.currentTarget.style.background='none'; 
            e.currentTarget.style.color='#00d4b8';
          }}>
          ← Back to site
        </button>
      </div>

      {/* ── CONTENT ── */}
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '64px 28px 100px' }}>

        {/* Header */}
        <div style={{ marginBottom: 48 }}>
          <div style={{
            display: 'inline-block',
            fontFamily: "'IBM Plex Mono', monospace", fontSize: 10,
            color: '#00d4b8', letterSpacing: '0.12em', textTransform: 'uppercase',
            marginBottom: 16,
          }}>Legal</div>
          <h1 style={{
            fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 700,
            lineHeight: 1.1, letterSpacing: '-0.025em', marginBottom: 16,
          }}>Privacy Policy</h1>
          <p style={{ fontSize: 14, color: 'rgba(220,232,245,0.45)', fontFamily: "'IBM Plex Mono', monospace" }}>
            Last updated: March 2026
          </p>
        </div>

        {/* Intro */}
        <Section>
          <p>
            Netwoke is a macOS network monitoring application. This privacy policy explains what data Netwoke
            collects, how it is used, and what we share with third parties. The short version: <strong>we collect
            almost nothing, and what we do collect never leaves your Mac.</strong>
          </p>
        </Section>

        <Divider />

        <Section title="1. Data We Collect">
          <p>Netwoke collects the following data <strong>locally on your Mac only</strong>:</p>
          <ul>
            <li><strong>Network connection metadata</strong> — app names, remote IP addresses, ports, protocols, and connection durations. This is read directly from macOS system tools (<code>lsof</code>, <code>netstat</code>) and is never transmitted anywhere.</li>
            <li><strong>System metrics</strong> — CPU load and memory usage, displayed in the dashboard. Never transmitted.</li>
            <li><strong>Alert history</strong> — a local in-memory log of alerts generated during your session. Cleared when the app closes.</li>
            <li><strong>Sound preference</strong> — whether you have sounds enabled or disabled, stored in your browser's localStorage. Never transmitted.</li>
          </ul>
          <p>We do <strong>not</strong> collect:</p>
          <ul>
            <li>Your name, email address, or any personal identifiers</li>
            <li>The content of any network connection (we see metadata only, not data inside connections)</li>
            <li>Browsing history or any information about what you do online</li>
            <li>Crash reports or diagnostic data</li>
            <li>Usage analytics of any kind</li>
          </ul>
        </Section>

        <Divider />

        <Section title="2. Third-Party Services">
          <p>Netwoke optionally connects to the following third-party services:</p>

          <SubSection title="Ollama (Local AI Explanations)">
            <p>
              Netwoke automatically detects if you have <strong>Ollama</strong> installed on your Mac. When you click "Ask AI to explain this" on a connection card, Netwoke uses your local Ollama instance to generate plain-English explanations of network connections.
              <br /><br />
              <strong>All AI processing happens entirely on your Mac.</strong> No connection metadata is sent to any external service. The AI explanations are generated locally using your own hardware, ensuring complete privacy.
              <br /><br />
              This feature is <strong>opt-in</strong> — it only activates when you explicitly click the button. If Ollama is not installed, Netwoke will not make any external API calls for AI explanations. You can review Ollama's privacy policy at{' '}
              <a href="https://ollama.com/privacy" target="_blank" rel="noopener noreferrer"
                style={{ color: '#00d4b8' }}>ollama.com/privacy</a>.
            </p>
          </SubSection>

         

          <SubSection title="MaxMind GeoLite2 (Geolocation)">
            <p>
              Netwoke uses the MaxMind GeoLite2 database for offline IP geolocation — determining which
              country a remote IP address belongs to. This database runs entirely on your Mac and makes
              no network requests. No data is sent to MaxMind.
            </p>
          </SubSection>

          <SubSection title="Stripe (Payments)">
            <p>
              Purchases are processed by Stripe. When you purchase Netwoke, your payment information is
              handled entirely by Stripe — we never see or store your card details. You can review
              Stripe's privacy policy at{' '}
              <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer"
                style={{ color: '#00d4b8' }}>stripe.com/privacy</a>.
            </p>
          </SubSection>
        </Section>

        <Divider />

        <Section title="3. Data Storage">
          <p>
            All data generated by Netwoke is stored locally on your Mac in memory during your session.
            No data is written to a remote server, cloud storage, or any external system. When you quit
            Netwoke, session data is cleared.
          </p>
          <p>
            The only persistent local storage Netwoke uses is a single localStorage key to remember your
            sound preference setting.
          </p>
        </Section>

        <Divider />

        <Section title="4. Network Access">
          <p>
            Netwoke requests macOS permission to monitor network connections. This permission is used
            exclusively to read connection metadata from the local system. Netwoke does not intercept,
            modify, or log the actual content of any network connection.
          </p>
          <p>
            Netwoke uses <code>pfctl</code> to add firewall rules when you use the Block IP feature.
            These rules are applied locally to your Mac's firewall only.
          </p>
        </Section>

        <Divider />

        <Section title="5. Children's Privacy">
          <p>
            Netwoke is not directed at children under the age of 13. We do not knowingly collect any
            information from children.
          </p>
        </Section>

        <Divider />

        <Section title="6. Changes to This Policy">
          <p>
            We may update this privacy policy from time to time. Changes will be reflected by updating
            the "Last updated" date at the top of this page. Continued use of Netwoke after changes
            constitutes acceptance of the updated policy.
          </p>
        </Section>

        <Divider />

        <Section title="7. Contact">
          <p>
            If you have any questions about this privacy policy or how Netwoke handles data, you can
            reach us at{' '}
            <a href="mailto:privacy@netwoke.app" style={{ color: '#00d4b8' }}>privacy@netwoke.app</a>.
          </p>
        </Section>

      </div>

      {/* ── FOOTER ── */}
      <div style={{
        borderTop: '1px solid rgba(255,255,255,0.07)',
        padding: '24px 28px',
        textAlign: 'center',
        fontSize: 11, color: 'var(--accent-teal)',
        fontFamily: "'IBM Plex Mono', monospace",
      }}>
        © {new Date().getFullYear()} Netwoke · Where Connection Meets Disclosure.
      </div>
    </div>
  )
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function Section({ title, children }) {
  return (
    <section style={{ marginBottom: 40 }}>
      {title && (
        <h2 style={{
          fontSize: 18, fontWeight: 600, marginBottom: 16,
          letterSpacing: '-0.01em', color: '#dce8f5',
        }}>{title}</h2>
      )}
      <div style={{
        fontSize: 14, color: 'rgba(220,232,245,0.7)',
        lineHeight: 1.85, fontWeight: 300,
      }}>
        {children}
      </div>
    </section>
  )
}

function SubSection({ title, children }) {
  return (
    <div style={{
      marginTop: 20, marginBottom: 20,
      paddingLeft: 16,
      borderLeft: '2px solid rgba(0,212,184,0.3)',
    }}>
      <h3 style={{
        fontSize: 14, fontWeight: 600, marginBottom: 8,
        color: '#dce8f5', letterSpacing: '-0.005em',
      }}>{title}</h3>
      <div style={{
        fontSize: 13, color: 'rgba(220,232,245,0.6)',
        lineHeight: 1.85, fontWeight: 300,
      }}>
        {children}
      </div>
    </div>
  )
}

function Divider() {
  return <div style={{ height: 1, background: 'rgba(255,255,255,0.06)', margin: '40px 0' }} />
}
